import { z } from 'zod'

import { request } from '@/shared/api/request'

/**
 * The Eleva API speaks snake_case JSON. Wire schemas below validate the
 * snake_case payloads and transform them into camelCase domain objects, so
 * the rest of the app never sees wire casing.
 */

const authResponseSchema = z
  .object({
    user_id: z.uuid(),
    username: z.string(),
    display_name: z.string().nullish(),
    avatar_url: z.string().nullish(),
    access_token: z.string(),
    refresh_token: z.string(),
    user_role: z.number(),
    is_new_user: z.boolean().optional(),
    deletion_cancelled: z.boolean().optional(),
  })
  .transform((wire) => ({
    userId: wire.user_id,
    username: wire.username,
    displayName: wire.display_name ?? null,
    avatarUrl: wire.avatar_url ?? null,
    accessToken: wire.access_token,
    refreshToken: wire.refresh_token,
    userRole: wire.user_role,
    isNewUser: wire.is_new_user ?? false,
    /** True when this sign-in aborted a pending account deletion. */
    deletionCancelled: wire.deletion_cancelled ?? false,
  }))

export type AuthResponse = z.infer<typeof authResponseSchema>

const meSchema = z
  .object({
    id: z.uuid(),
    email: z.string(),
    username: z.string(),
    display_name: z.string().nullish(),
    avatar_url: z.string().nullish(),
    bio: z.string().nullish(),
    country: z.string().nullish(),
    country_code: z.string().nullish(),
    kyc_status: z.number(),
    is_verified: z.boolean(),
    role: z.number(),
    agreements_accepted_at: z.string().nullish(),
    created_at: z.string(),
    /** AuthProviders flags: 1 = email, 2 = Google, 4 = Apple. */
    auth_provider: z.number().optional(),
    provider_email: z.string().nullish(),
    /** Days between confirming account deletion and the actual delete. */
    deletion_grace_days: z.number().optional(),
  })
  .transform((wire) => ({
    id: wire.id,
    email: wire.email,
    username: wire.username,
    displayName: wire.display_name ?? null,
    avatarUrl: wire.avatar_url ?? null,
    bio: wire.bio ?? null,
    country: wire.country ?? null,
    countryCode: wire.country_code ?? null,
    kycStatus: wire.kyc_status,
    isVerified: wire.is_verified,
    role: wire.role,
    agreementsAcceptedAt: wire.agreements_accepted_at ?? null,
    createdAt: wire.created_at,
    authProvider: wire.auth_provider ?? 1,
    providerEmail: wire.provider_email ?? null,
    deletionGraceDays: wire.deletion_grace_days ?? 30,
    /** Provider-only accounts (Google/Apple) have no password yet. */
    hasPassword: ((wire.auth_provider ?? 1) & 1) !== 0,
  }))

export type Me = z.infer<typeof meSchema>

export interface RegisterPayload {
  email: string
  password: string
  displayName: string
  /** ISO date, yyyy-MM-dd (native <input type="date"> value). */
  birthDate: string
  /** ISO 3166-1 alpha-2 picked on the form (defaults from the browser locale). */
  country: string
}

export type SocialProvider = 'google' | 'apple'

export interface SocialSignInPayload {
  /** The provider's identity token (Google ID token / Apple identity token). */
  idToken: string
  /**
   * Sign-up only, ISO date yyyy-MM-dd. Without it the call is a plain login
   * and an unknown user gets `account_not_found`.
   */
  birthDate?: string
  /** Sign-up only; the server falls back to the provider's profile name. */
  displayName?: string
  /** Sign-up only, ISO 3166-1 alpha-2 picked on the form. */
  country?: string
}

const BASE = '/api/eleva/auth'

/** Server-side purposes for the 6-digit email codes (VerificationPurpose). */
export const CODE_PURPOSE = {
  changePassword: 20,
  changeEmail: 30,
  setPassword: 40,
  changeUsername: 70,
  deleteAccount: 80,
} as const

export type CodePurpose = (typeof CODE_PURPOSE)[keyof typeof CODE_PURPOSE]

export const authApi = {
  login: (payload: { email: string; password: string }) =>
    request(
      {
        url: `${BASE}/login`,
        method: 'POST',
        data: { email: payload.email, password: payload.password, device_info: null },
      },
      authResponseSchema,
    ),

  /**
   * Google / Apple sign-in and sign-up share one endpoint per provider: an
   * existing account logs in, a new one is created when the sign-up fields
   * travel along. Provider accounts are verified on the spot - no email code.
   */
  socialSignIn: (provider: SocialProvider, payload: SocialSignInPayload) =>
    request(
      {
        url: `${BASE}/${provider}`,
        method: 'POST',
        data: {
          id_token: payload.idToken,
          ...(payload.displayName ? { display_name: payload.displayName } : {}),
          ...(payload.birthDate ? { birth_date: payload.birthDate } : {}),
          ...(payload.country ? { country_code: payload.country } : {}),
          device_info: null,
        },
      },
      authResponseSchema,
    ),

  /** Creates the account and sends the confirmation email. No tokens back. */
  register: (payload: RegisterPayload) =>
    request<void>({
      url: `${BASE}/register`,
      method: 'POST',
      data: {
        email: payload.email,
        password: payload.password,
        display_name: payload.displayName,
        birth_date: payload.birthDate,
        country_code: payload.country || null,
      },
    }),

  /** Confirms the email address using the token from the confirmation link. */
  confirm: (token: string) =>
    request<void>({ url: `${BASE}/confirm`, method: 'POST', data: { token } }),

  /** Verifies the 6-digit registration code; the account can sign in afterwards. */
  confirmCode: (payload: { email: string; code: string }) =>
    request<void>({ url: `${BASE}/confirm-code`, method: 'POST', data: payload }),

  /** Re-sends the registration confirmation code. */
  resendCode: (email: string) =>
    request<void>({ url: `${BASE}/resend-code`, method: 'POST', data: { email } }),

  /**
   * Requests a code for a sensitive action; for `changeEmail` the code is
   * sent to (and verifies) the new address.
   */
  requestCode: (purpose: CodePurpose, newEmail?: string) =>
    request<void>({
      url: `${BASE}/request-code`,
      method: 'POST',
      data: { purpose, ...(newEmail ? { new_email: newEmail } : {}) },
    }),

  /** Emails a 6-digit reset code to the account. */
  forgotPassword: (email: string) =>
    request<void>({ url: `${BASE}/forgot-password`, method: 'POST', data: { email } }),

  /** Sets a new password using the emailed reset code. */
  resetPassword: (payload: { email: string; code: string; password: string }) =>
    request<void>({ url: `${BASE}/reset-password`, method: 'POST', data: payload }),

  changePassword: (payload: { oldPassword: string; newPassword: string; code: string }) =>
    request<void>({
      url: `${BASE}/change-password`,
      method: 'POST',
      data: {
        old_password: payload.oldPassword,
        new_password: payload.newPassword,
        code: payload.code,
      },
    }),

  /** Sets a password on a provider-only account (verified by code). */
  setPassword: (payload: { newPassword: string; code: string }) =>
    request<void>({
      url: `${BASE}/set-password`,
      method: 'POST',
      data: { new_password: payload.newPassword, code: payload.code },
    }),

  /**
   * Switches the login email: requires the password plus the codes sent to
   * the current and the new address.
   */
  changeEmail: (payload: { password: string; oldCode: string; newCode: string }) =>
    request<void>({
      url: `${BASE}/change-email`,
      method: 'POST',
      data: {
        password: payload.password,
        old_code: payload.oldCode,
        new_code: payload.newCode,
      },
    }),

  /** Changes the username after the availability check and the emailed code. */
  changeUsername: (payload: { username: string; code: string }) =>
    request<void>({ url: `${BASE}/change-username`, method: 'POST', data: payload }),

  checkUsernameAvailable: async (username: string): Promise<boolean> => {
    const data = await request<Record<string, unknown>>({
      url: '/api/eleva/usernames/check',
      params: { username },
    })
    return Boolean(data?.available)
  },

  /**
   * Schedules the account for deletion after the server's grace period.
   * Password is required when the account has one. `deleteContent` is the
   * user's answer about their posts and pages: erased with the account
   * (true) or moved to the @origin account (false) once the period ends.
   */
  deleteAccount: (payload: { code: string; password?: string; deleteContent: boolean }) =>
    request<void>({
      url: `${BASE}/delete-account`,
      method: 'POST',
      data: {
        code: payload.code,
        ...(payload.password ? { password: payload.password } : {}),
        delete_content: payload.deleteContent,
      },
    }),

  logout: () => request<void>({ url: `${BASE}/logout`, method: 'POST' }),

  /**
   * Anonymous support message - the only channel for users whose account is
   * blocked (regular support tickets require a session they no longer have).
   */
  contact: (payload: { name: string; email: string; subject: string; message: string }) =>
    request<void>({
      url: '/api/eleva/contact',
      method: 'POST',
      data: {
        name: payload.name,
        email: payload.email,
        subject: payload.subject,
        message: payload.message,
      },
    }),

  me: (signal?: AbortSignal) => request({ url: `${BASE}/me`, method: 'GET', signal }, meSchema),
}
